import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { stockMovementSchema } from './inventory';

/* Stock transfers (ADR-0025). Quantities: `unit` at scale 1000. */

export const stockTransferLineSchema = z.object({
  id: uuidSchema,
  line_no: z.number().int(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  unit: z.string(),
  qty_scaled: z.number().int(),
  qty_base_scaled: z.number().int(),
  override_reason: z.string().nullable(),
  value_minor: z.number().int().nullable(),
});

export const stockTransferSchema = z.object({
  id: uuidSchema,
  transfer_number: z.string(),
  status: z.enum(['draft', 'in_transit', 'received', 'cancelled']),
  branch_id: uuidSchema,
  branch_name: z.string(),
  from_location_id: uuidSchema,
  from_location_name: z.string(),
  to_branch_id: uuidSchema,
  to_branch_name: z.string(),
  to_location_id: uuidSchema,
  to_location_name: z.string(),
  /** Whether the transfer leaves the branch (dispatch and receive are separate steps). */
  inter_branch: z.boolean(),
  notes: z.string().nullable(),
  currency: z.string(),
  value_minor: z.number().int().nullable(),
  created_by_name: z.string().nullable(),
  dispatched_by_name: z.string().nullable(),
  dispatched_at: isoTimestampSchema.nullable(),
  received_by_name: z.string().nullable(),
  received_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  lines: z.array(stockTransferLineSchema),
});
export type StockTransfer = z.infer<typeof stockTransferSchema>;
export const stockTransferListResponseSchema = z.object({
  items: z.array(
    stockTransferSchema.omit({ lines: true }).extend({ line_count: z.number().int() }),
  ),
});
export const stockTransferListQuerySchema = z.object({
  status: z.enum(['draft', 'in_transit', 'received', 'cancelled']).optional(),
  /** `outgoing` = created here; `incoming` = to be received here. Default both. */
  direction: z.enum(['outgoing', 'incoming']).optional(),
});

const lineInput = z.object({
  item_id: uuidSchema,
  lot_id: uuidSchema.nullable().default(null),
  unit: z.string().trim().min(1).max(12).optional(),
  qty_scaled: z.number().int().positive().refine(Number.isSafeInteger),
  /** Required (with `inventory.approve`) to move a held or expired lot. */
  override_reason: z.string().trim().min(3).max(300).nullable().default(null),
});
export const createStockTransferRequestSchema = z.object({
  from_location_id: uuidSchema,
  /** Default: this branch. */
  to_branch_id: uuidSchema.optional(),
  to_location_id: uuidSchema,
  notes: z.string().trim().min(1).max(300).nullable().default(null),
  lines: z.array(lineInput).min(1).max(100),
});
export const stockTransferActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const stockTransferPostResultSchema = z.object({
  transfer: stockTransferSchema,
  movements: z.array(stockMovementSchema),
});
